import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import {FmlxIcon, FmlxTooltip, FmlxTextBox} from 'fmlx-common-ui';
import {Button} from 'react-bootstrap';
import FastSwitch from 'components/Switch/FastSwitch';
import Spinner from '../../../../components/Spinner/Spinner';
import TaskNumber from '../../Components/TaskNumber';

import withIntl from '../../../../lib/withIntl';
import {useArrowDownPress, useArrowUpPress, useClickOutside, useEnterPress} from '../../../../lib/customHooks';

import {ExecutionStatus, TaskType} from '../../../../constants/dataEnum';

import './TaskConfirmation.scss';
import TaskListCheckbox from '../Components/TaskListCheckbox';

function TaskConfirmationView({
  confirmation,
  ID,
  task,
  number,
  isIgnored,
  onCheckedChange,
  onEdit,
  onDelete,
  runtimeMode,
  executionStatus,
  isCardMode,
  isDragging,
  onSelectTask,
  isHighlightedTask,
  intl,
  onEjectTrayChange,
  onArrowUpPress,
  onArrowDownPress,
  isViewMode,
}) {
  const taskRef = useRef();
  const onClickOutside = () => {
    if (isHighlightedTask && isCardMode && onSelectTask) {
      onSelectTask(ID);
    }
  };

  const onEjectChange = () => {
    const isEject = !(task.type === TaskType.USER_CONFIRMATION_EJECT);
    const newTask = {
      ...task,
      type: isEject ? TaskType.USER_CONFIRMATION_EJECT : TaskType.USER_CONFIRMATION,
    };
    onEjectTrayChange(newTask);
  };

  useArrowUpPress(taskRef, onArrowUpPress);
  useArrowDownPress(taskRef, onArrowDownPress);
  useClickOutside(taskRef, onClickOutside);
  return (
    <div
      ref={taskRef}
      className={`task-confirmation-view ${isDragging && 'dragging'} ${isHighlightedTask && 'selected'}`}
      role="presentation"
      onClick={() => isCardMode && onSelectTask(task?.ID)}
    >
      {!isCardMode && <TaskNumber number={number} isHighlighted={isHighlightedTask} />}
      <span className="task-confirmation-view-content">
        <span className="task-confirmation-view-icon">
          <img src="./svg/protocol-editor/confirmation-blue.svg" alt="Confirmation" />
        </span>
        {intl.formatMessage({id: 'protocolEditor.taskList.taskConfirmation.contentTitle'})}
      </span>
      {!runtimeMode && !isViewMode && (
        <>
          <span
            className={`task-confirmation-view-action icon-edit-2 ${isCardMode && 'card-mode'}`}
            onClick={(e) => {
              onEdit();
              e.stopPropagation();
            }}
            role="presentation"
          />
          {!isCardMode && (
            <span
              className="task-confirmation-view-delete icon-trash"
              onClick={(e) => {
                onDelete(ID);
                e.stopPropagation();
              }}
              role="presentation"
            />
          )}
          {!isCardMode && (
            <TaskListCheckbox
              checked={!isIgnored}
              onChange={(e) => {
                onCheckedChange(ID, e.target.checked);
              }}
            />
          )}
        </>
      )}
      <div className="task-confirmation-view-message">
        <span className="title">
          {intl.formatMessage({id: 'protocolEditor.taskList.taskConfirmation.messageTitle'})}
        </span>
        <span className="message break-word">{confirmation}</span>
        {!runtimeMode && !isViewMode && (
          <div
            className="task-reuse-tips"
            role="presentation"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <FastSwitch size="thin" checked={task.type === TaskType.USER_CONFIRMATION_EJECT} onChange={onEjectChange} />
            &nbsp;
            <span>{intl.formatMessage({id: 'protocolEditor.taskList.confirmation.ejectTray'})}</span>
            &nbsp;
            <FmlxTooltip title={intl.formatMessage({id: 'protocolEditor.taskList.confirmation.ejectTray.info'})}>
              <span>
                <FmlxIcon name="InfoOutline" size="xs" customColor="#414141" />
              </span>
            </FmlxTooltip>
          </div>
        )}
      </div>
      {runtimeMode && executionStatus === ExecutionStatus.EXECUTING && <Spinner />}
      {runtimeMode && executionStatus === ExecutionStatus.EXECUTED && (
        <img className="executed" src="./svg/protocol-editor/execution-done.svg" alt="Executed" />
      )}
    </div>
  );
}

TaskConfirmationView.propTypes = {
  confirmation: PropTypes.string.isRequired,
  ID: PropTypes.number.isRequired,
  task: PropTypes.object.isRequired,
  number: PropTypes.number,
  isHighlightedTask: PropTypes.bool.isRequired,
  onSelectTask: PropTypes.func.isRequired,
  isIgnored: PropTypes.bool.isRequired,
  onCheckedChange: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  runtimeMode: PropTypes.bool.isRequired,
  executionStatus: PropTypes.string.isRequired,
  isCardMode: PropTypes.bool.isRequired,
  isDragging: PropTypes.bool.isRequired,
  intl: PropTypes.any.isRequired,
  onEjectTrayChange: PropTypes.func.isRequired,
  onArrowUpPress: PropTypes.func.isRequired,
  onArrowDownPress: PropTypes.func.isRequired,
  isViewMode: PropTypes.bool.isRequired,
};

TaskConfirmationView.defaultProps = {
  number: undefined,
};

function TaskConfirmationEditor({initialTask, onApply, excludedComponentRef, intl}) {
  const [task, setTask] = useState(initialTask);

  const confirmationRef = useRef();
  const textBoxRef = useRef();

  useEffect(() => {
    textBoxRef.current.focus();
  }, []);

  function doApply() {
    onApply(task);
  }

  useClickOutside(confirmationRef, doApply, excludedComponentRef);

  useEnterPress(textBoxRef, doApply);

  return (
    <div
      ref={confirmationRef}
      className="task-confirmation-edit"
      onClick={(e) => e.stopPropagation()}
      role="presentation"
    >
      <div className="task-confirmation-edit-title blue">
        {intl.formatMessage({id: 'protocolEditor.taskList.taskConfirmation.title'})}
      </div>
      <FmlxTextBox
        ref={textBoxRef}
        mode="textarea"
        value={task.message}
        onChange={({value}) => {
          setTask((prevTask) => ({...prevTask, message: value}));
        }}
      />
      <div className="task-confirmation-bottom">
        <div
          className="task-reuse-tips"
          role="presentation"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <FastSwitch
            size="xxs"
            checked={task.type === TaskType.USER_CONFIRMATION_EJECT}
            onChange={(checked) => {
              setTask((prevTask) => ({
                ...prevTask,
                type: checked ? TaskType.USER_CONFIRMATION_EJECT : TaskType.USER_CONFIRMATION,
              }));
            }}
          />
          &nbsp;
          <span>{intl.formatMessage({id: 'protocolEditor.taskList.confirmation.ejectTray'})}</span>
          &nbsp;
          <FmlxTooltip title={intl.formatMessage({id: 'protocolEditor.taskList.confirmation.ejectTray.info'})}>
            <span>
              <FmlxIcon name="InfoOutline" size="xs" customColor="#414141" />
            </span>
          </FmlxTooltip>
        </div>
        <Button bsStyle="success" disabled={!task.message} onClick={doApply}>
          {intl.formatMessage({id: 'protocolEditor.taskList.taskDelay.apply'})}
        </Button>
      </div>
    </div>
  );
}

TaskConfirmationEditor.propTypes = {
  initialTask: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
  excludedComponentRef: PropTypes.array.isRequired,
  intl: PropTypes.any.isRequired,
};

function TaskConfirmation({
  ID,
  initialTask,
  onApply,
  number,
  isIgnored,
  onCheckedChange,
  onDelete,
  runtimeMode,
  executionStatus,
  isCardMode,
  intl,
  drag,
  isDragging,
  onSelectTask,
  onArrowUpPress,
  onArrowDownPress,
  isHighlightedTask,
  deleteModalConfirmationShowing,
  excludedComponentRef,
  isViewMode,
}) {
  const [editMode, setEditMode] = useState(!runtimeMode && !initialTask.message);

  useEffect(() => {
    setEditMode(!runtimeMode && !initialTask.message);
  }, [initialTask.message, runtimeMode]);

  const applyEdit = (newTask) => {
    if (deleteModalConfirmationShowing) {
      return;
    }
    setEditMode(false);
    onApply(newTask);
  };

  return (
    <div className="wrapper" ref={(el) => drag && drag(el)}>
      {editMode && (
        <TaskConfirmationEditor
          initialTask={initialTask}
          onApply={(newTask) => applyEdit(newTask)}
          excludedComponentRef={excludedComponentRef}
          intl={intl}
        />
      )}
      {!editMode && (
        <TaskConfirmationView
          ID={ID}
          task={initialTask}
          confirmation={initialTask.message}
          onEdit={() => setEditMode(true)}
          runtimeMode={runtimeMode}
          executionStatus={executionStatus}
          number={number}
          onCheckedChange={onCheckedChange}
          onDelete={onDelete}
          drag={drag}
          isDragging={isDragging}
          isCardMode={isCardMode}
          isIgnored={isIgnored}
          onSelectTask={onSelectTask}
          isHighlightedTask={isHighlightedTask}
          intl={intl}
          onEjectTrayChange={(newTask) => onApply(newTask)}
          onArrowDownPress={onArrowDownPress}
          onArrowUpPress={onArrowUpPress}
          isViewMode={isViewMode}
        />
      )}
    </div>
  );
}

TaskConfirmation.propTypes = {
  ID: PropTypes.number,
  initialTask: PropTypes.object,
  onApply: PropTypes.func,
  number: PropTypes.number,
  isIgnored: PropTypes.bool.isRequired,
  onCheckedChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  intl: PropTypes.any.isRequired,
  runtimeMode: PropTypes.bool,
  executionStatus: PropTypes.string,
  isCardMode: PropTypes.bool,
  drag: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  onSelectTask: PropTypes.func.isRequired,
  onArrowUpPress: PropTypes.func,
  onArrowDownPress: PropTypes.func,
  isHighlightedTask: PropTypes.bool.isRequired,
  deleteModalConfirmationShowing: PropTypes.bool.isRequired,
  excludedComponentRef: PropTypes.array,
  isViewMode: PropTypes.bool.isRequired,
};

TaskConfirmation.defaultProps = {
  ID: 0,
  initialTask: {
    ID: 0,
    message: '',
  },
  onApply: () => {},
  runtimeMode: false,
  executionStatus: ExecutionStatus.NONE,
  number: undefined,
  isCardMode: undefined,
  excludedComponentRef: [],
  onArrowUpPress: () => {},
  onArrowDownPress: () => {},
};

export default withIntl(TaskConfirmation);
